"use client";

// ─── Sugestia zestawu (upsell) ────────────────────────────────────
// Jeśli pojedyncze kursy w koszyku tworzą dostępny zestaw, proponujemy zakup
// w niższej cenie. Kliknięcie zamienia pojedyncze kursy na zestaw (zachowując
// poprawną cenę). Nie pokazujemy sugestii, gdy zestaw już jest w koszyku.

import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Boxes, Sparkles, ArrowRight } from "lucide-react";
import { useCart, type CartItem } from "@/lib/cart";
import { formatPln, type BundleView } from "@/lib/bundles-shared";

const ACCENT = "#7c3aed";

export default function BundleUpsell({ variant = "cart" }: { variant?: "cart" | "checkout" }) {
  const { items, addBundle } = useCart();
  const [suggestions, setSuggestions] = useState<BundleView[]>([]);

  // Pojedyncze kursy i materiały w koszyku (bez zestawów).
  const singleCourseIds = useMemo(
    () => items.filter((i) => i.kind !== "bundle" && i.kind !== "material").map((i) => i.id),
    [items]
  );
  const singleMaterialIds = useMemo(
    () => items.filter((i) => i.kind === "material").map((i) => i.id),
    [items]
  );
  const key = useMemo(
    () => [...singleCourseIds, ...singleMaterialIds.map((m) => `m:${m}`)].sort().join(","),
    [singleCourseIds, singleMaterialIds]
  );
  const hasBundleInCart = items.some((i) => i.kind === "bundle");

  useEffect(() => {
    if (singleCourseIds.length + singleMaterialIds.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    fetch("/api/bundles/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ courseIds: singleCourseIds, materialIds: singleMaterialIds }),
    })
      .then((r) => (r.ok ? r.json() : { bundles: [] }))
      .then((d) => {
        if (!cancelled) setSuggestions(Array.isArray(d.bundles) ? d.bundles : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Pokaż tylko zestawy w pełni pokryte pojedynczymi produktami i jeszcze niedodane.
  const courseSet = new Set(singleCourseIds);
  const materialSet = new Set(singleMaterialIds);
  const best = suggestions.find(
    (b) => b.courses.every((c) => courseSet.has(c.id)) && b.materials.every((m) => materialSet.has(m.id))
  );

  if (!best || best.savings <= 0) return null;

  const swap = () => {
    const item: CartItem = {
      id: `bundle:${best.id}`,
      slug: best.slug,
      title: best.name,
      price: best.bundlePrice,
      originalPrice: best.comparePrice,
      thumbnail: best.thumbnail,
      cardColor: ACCENT,
      kind: "bundle",
      bundleId: best.id,
      bundleCourses: best.courses.map((c) => ({ id: c.id, title: c.title, price: c.price })),
      bundleMaterials: best.materials.map((m) => ({ id: m.id, title: m.title, price: m.price })),
      giftable: best.giftable,
    };
    addBundle(item);
  };

  // Strona checkout jest zawsze jasna — na niej pomijamy warianty dark:,
  // które inaczej dałyby prawie biały tekst na jasnym tle (niewidoczny).
  const isCheckout = variant === "checkout";
  const dk = (classes: string) => (isCheckout ? "" : classes);

  return (
    <AnimatePresence>
      {!hasBundleInCart && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className={`rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 p-4 ${dk(
            "dark:border-violet-500/20 dark:from-violet-500/10 dark:to-indigo-500/10"
          )} ${isCheckout ? "" : "mb-3"}`}
        >
          <div className={`flex items-center gap-2 text-sm font-bold text-violet-800 ${dk("dark:text-violet-200")}`}>
            <Sparkles className="h-4 w-4" /> Możesz kupić te kursy taniej w zestawie!
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500 text-white shrink-0">
              <Boxes className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`truncate text-sm font-semibold text-slate-800 ${dk("dark:text-slate-100")}`}>{best.name}</p>
              <p className={`text-xs text-slate-500 ${dk("dark:text-slate-400")}`}>
                Razem <span className="line-through">{formatPln(best.comparePrice)}</span> →{" "}
                <span className={`font-bold text-violet-700 ${dk("dark:text-violet-300")}`}>{formatPln(best.bundlePrice)}</span>
              </p>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs font-bold text-emerald-600">Oszczędzasz {formatPln(best.savings)}</span>
            <button
              onClick={swap}
              className="inline-flex items-center gap-1.5 rounded-xl bg-violet-500 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-violet-600"
            >
              Kup jako zestaw i oszczędź <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
